/**
 * MultiFloorCanvas - Container for multiple floor canvases
 * Handles grid, stack, and side-by-side layouts
 */

import { useRef, useEffect } from 'react';
import { Floor, LayoutMode } from '../../../../map-editor-shared/store';
import { FloorBadge } from '../FloorBadge';
import { StatusOverlay } from '../../../../map-editor-features/status';
import { EmergencyBellClickLayer } from './EmergencyBellClickLayer';
import type { SimulatorEmergencyBell } from '../../../../models/Simulator';
import { useJointJSCanvas } from '../../../../map-editor-pages/editor/hooks/useJointJSCanvas';
import { useViewerPanning } from '../../hooks/useViewerPanning';
import { useCanvasZoom } from '../../../../map-editor-pages/editor/hooks/useCanvasZoom';
import { useTheme } from '../../../../map-editor-shared/context/ThemeContext';
import { useThemeSync } from '../../../../map-editor-pages/editor/hooks/useThemeSync';
import { log, LogCategory } from '../../../../map-editor-shared/lib/logger';
import {
  mapDataGetCsvParsed,
  mapDataGetCsvRaw,
} from '../../../../map-editor-shared/store/mapDataCsvHelpers';
import styles from './MultiFloorCanvas.module.css';

/**
 * 비상벨 클릭 레이어 opt-in 설정. 전달되면 시뮬레이터 비상벨 탭에서
 * 맵 위 비상벨 마커 클릭을 활성화한다. 미전달(기본)이면 에디터/뷰어와 동일하게
 * 클릭 레이어를 마운트하지 않아 동작이 byte-for-byte 동일하다.
 */
export interface BellClickConfig {
  bells: SimulatorEmergencyBell[];
}

export interface MultiFloorCanvasProps {
  floors: Floor[];
  layout: LayoutMode;
  synchronizedZoom?: boolean;
  onZoomChange?: (floorId: string, zoom: number) => void;
  /** opt-in: 비상벨 맵 클릭 레이어 설정 (시뮬레이터 비상벨 탭 전용). */
  bellClick?: BellClickConfig;
  /** opt-in: 레이아웃별 최소 높이(그리드 400px 등)를 강제하지 않고 부모 크기에 맞춰 줄어들게 한다 — 자유 리사이즈 가능한 대시보드 위젯 등에서 사용. */
  compact?: boolean;
}

interface FloorCanvasProps {
  floor: Floor;
  layout: LayoutMode;
  synchronizedZoom?: boolean;
  externalZoom?: number;
  onZoomChange?: (zoom: number) => void;
  /** opt-in: 비상벨 맵 클릭 레이어 설정. */
  bellClick?: BellClickConfig;
  /** opt-in: 레이아웃 최소 높이를 강제하지 않는다. */
  compact?: boolean;
}

/**
 * Individual floor canvas component
 */
function FloorCanvas({
  floor,
  layout,
  synchronizedZoom = false,
  externalZoom,
  onZoomChange,
  bellClick,
  compact = false,
}: FloorCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const canvasId = `floor-canvas-${floor.id}`; // Unique ID for each floor

  // JointJS Canvas - pass unique canvasId
  const { graph, paper } = useJointJSCanvas(canvasRef, canvasId);

  // Canvas interactions (read-only)
  useViewerPanning(paper, canvasRef);
  useCanvasZoom(paper, canvasRef, (newZoom) => {
    if (onZoomChange) {
      onZoomChange(newZoom);
    }
  });
  useThemeSync(paper, theme);

  // Load graph from floor data
  useEffect(() => {
    log.debug(LogCategory.FLOOR, `FloorCanvas[${floor.name}]: Attempting to load graph...`);
    log.debug(LogCategory.FLOOR, `Dependencies check:`, {
      hasGraph: !!graph,
      hasPaper: !!paper,
      hasMapData: !!floor.mapData,
      hasGraphJson: !!floor.mapData?.graphJson,
      graphJsonType: typeof floor.mapData?.graphJson,
      floorId: floor.id,
      floorName: floor.name
    });

    // FIX: Allow rendering when floor has CSV data OR graphJson
    // CSV-only floors should also be renderable in the viewer
    const hasGraphJson = !!floor.mapData?.graphJson;
    const hasCsvData =
      !!mapDataGetCsvParsed(floor.mapData) || !!mapDataGetCsvRaw(floor.mapData);
    const hasAnyData = hasGraphJson || hasCsvData;

    if (!graph || !paper || !hasAnyData) {
      log.warn(LogCategory.FLOOR, `FloorCanvas[${floor.name}]: Missing dependencies, skipping render`, {
        missingGraph: !graph,
        missingPaper: !paper,
        missingMapData: !floor.mapData,
        hasGraphJson,
        hasCsvData,
        hasAnyData
      });
      return;
    }

    try {
      // Clear existing graph
      graph.clear();

      // FIX: Only load graphJson if it exists, CSV-only floors will show empty canvas
      if (floor.mapData.graphJson) {
        // Load graph data
        graph.fromJSON(floor.mapData.graphJson);
      }

      // Store floor context in graph metadata for StatusOverlay
      graph.set('floorId', floor.id);

      const cellCount = graph.getCells().length;
      log.info(LogCategory.FLOOR, `FloorCanvas[${floor.name}]: Loaded ${cellCount} cells, floorId: ${floor.id}`);

      // Log z-index distribution
      const zIndexCounts = new Map<number, number>();
      graph.getCells().forEach(cell => {
        if (cell.isElement()) {
          const z = cell.get('z') || 0;
          zIndexCounts.set(z, (zIndexCounts.get(z) || 0) + 1);
        }
      });
      log.debug(LogCategory.FLOOR, `FloorCanvas[${floor.name}]: Z-index distribution:`, Array.from(zIndexCounts.entries()).sort((a, b) => a[0] - b[0]));

      // Fit content to screen after rendering
      setTimeout(() => {
        if (cellCount > 0 && paper && graph) {
          // Double-check cells still exist before fitting
          const currentCells = graph.getCells();
          if (currentCells.length === 0) {
            log.warn(LogCategory.FLOOR, `FloorCanvas[${floor.name}]: Cells disappeared, skipping fit`);
            return;
          }

          const beforeScale = paper.scale();
          const beforeTranslate = paper.translate();
          log.debug(LogCategory.FLOOR, `FloorCanvas[${floor.name}]: Before fit - scale:${beforeScale.sx.toFixed(2)}, translate:(${beforeTranslate.tx.toFixed(0)}, ${beforeTranslate.ty.toFixed(0)})`);

          try {
            paper.scaleContentToFit({
              padding: 30,
              maxScale: 4,
              minScale: 0.01,
            });

            const afterScale = paper.scale();
            const afterTranslate = paper.translate();
            log.debug(LogCategory.FLOOR, `FloorCanvas[${floor.name}]: After fit - scale:${afterScale.sx.toFixed(2)}, translate:(${afterTranslate.tx.toFixed(0)}, ${afterTranslate.ty.toFixed(0)})`);
            log.info(LogCategory.FLOOR, `FloorCanvas[${floor.name}]: Fitted to viewport`);
          } catch (error) {
            log.error(LogCategory.FLOOR, `FloorCanvas[${floor.name}]: Failed to fit content:`, error);
          }
        }
      }, 100);
    } catch (error) {
      log.error(LogCategory.FLOOR, `FloorCanvas[${floor.name}]: Failed to load graph:`, error);
    }
  }, [graph, paper, floor.id]);

  // Set paper to read-only mode
  useEffect(() => {
    if (!paper) return;

    paper.setInteractivity(() => ({
      elementMove: false,
      addLinkFromMagnet: false,
    }));
  }, [paper]);

  // Resize paper to fit container
  useEffect(() => {
    if (!paper || !canvasRef.current) return;

    const updatePaperSize = () => {
      const canvas = canvasRef.current;
      const parentElement = canvas?.parentElement;

      log.debug(LogCategory.CANVAS, `FloorCanvas[${floor.name}]: Checking sizes...`);
      log.debug(LogCategory.CANVAS, `canvas: ${canvas?.clientWidth} x ${canvas?.clientHeight}`);
      log.debug(LogCategory.CANVAS, `parent: ${parentElement?.clientWidth} x ${parentElement?.clientHeight}`);
      log.debug(LogCategory.CANVAS, `parent classList: ${parentElement?.className}`);

      if (!parentElement) {
        log.warn(LogCategory.CANVAS, `FloorCanvas[${floor.name}]: No parent element found`);
        return;
      }

      const width = parentElement.clientWidth;
      const height = parentElement.clientHeight;

      if (width > 0 && height > 0) {
        log.debug(LogCategory.CANVAS, `FloorCanvas[${floor.name}]: Resizing paper to ${width}x${height}`);
        paper.setDimensions(width, height);

        // Re-fit content after resize
        const cellCount = graph?.getCells().length || 0;
        if (cellCount > 0 && graph) {
          setTimeout(() => {
            // Double-check cells still exist before fitting
            const currentCells = graph.getCells();
            if (currentCells.length === 0) {
              log.warn(LogCategory.CANVAS, `FloorCanvas[${floor.name}]: No cells to fit after resize`);
              return;
            }

            try {
              paper.scaleContentToFit({
                padding: 30,
                maxScale: 4,
                minScale: 0.01,
              });
              log.info(LogCategory.CANVAS, `FloorCanvas[${floor.name}]: Content fitted to ${width}x${height}`);
            } catch (error) {
              log.error(LogCategory.CANVAS, `FloorCanvas[${floor.name}]: Failed to fit after resize:`, error);
            }
          }, 50);
        }
      } else {
        log.warn(LogCategory.CANVAS, `FloorCanvas[${floor.name}]: Invalid size ${width}x${height}`);
      }
    };

    // Initial size update with delay to ensure DOM is ready
    setTimeout(() => {
      updatePaperSize();
    }, 100);

    const resizeObserver = new ResizeObserver(() => {
      updatePaperSize();
    });

    const parentElement = canvasRef.current?.parentElement;
    if (parentElement) {
      resizeObserver.observe(parentElement);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [paper, floor.id, graph]);

  // Sync zoom if enabled
  useEffect(() => {
    if (synchronizedZoom && externalZoom !== undefined && paper) {
      paper.scale(externalZoom, externalZoom);
    }
  }, [synchronizedZoom, externalZoom, paper]);

  // Calculate object count from graphJson cells (excluding links)
  const objectCount = floor.mapData?.graphJson?.cells
    ? floor.mapData.graphJson.cells.filter((cell: any) => cell.type !== 'standard.Link').length
    : 0;
  // FIX: Check for CSV data as well as graphJson
  const hasGraphData =
    !!floor.mapData?.graphJson ||
    !!mapDataGetCsvParsed(floor.mapData) ||
    !!mapDataGetCsvRaw(floor.mapData);

  return (
    <div className={`${styles.floorCanvas} ${styles[layout]} ${compact ? styles.compact : ''}`}>
      {!hasGraphData ? (
        <div className={styles.emptyState}>
          <p>{floor.name}: 맵 데이터 없음</p>
          <span style={{fontSize: '12px', color: '#888'}}>이 층에는 저장된 맵이 없습니다</span>
        </div>
      ) : (
        <>
          <div ref={canvasRef} id={canvasId} className={styles.canvas} />

          {/* Floor Badge */}
          <FloorBadge
            floor={floor}
            objectCount={objectCount}
            statusSummary="ok"
          />

          {/* Status Overlay */}
          {graph && paper && (
            <StatusOverlay graph={graph} paper={paper} />
          )}

          {/* 비상벨 클릭 레이어 (opt-in) — bellClick 이 있을 때만 마운트 */}
          {graph && paper && bellClick && (
            <EmergencyBellClickLayer
              graph={graph}
              paper={paper}
              bells={bellClick.bells}
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * Multi-floor canvas container with layout management
 */
export function MultiFloorCanvas({
  floors,
  layout,
  synchronizedZoom = false,
  onZoomChange,
  bellClick,
  compact = false,
}: MultiFloorCanvasProps) {
  const masterZoomRef = useRef<number>(1);

  // Handle zoom change from individual canvas
  const handleFloorZoomChange = (floorId: string, zoom: number) => {
    if (synchronizedZoom) {
      masterZoomRef.current = zoom;
    }

    if (onZoomChange) {
      onZoomChange(floorId, zoom);
    }
  };

  // Determine grid columns based on floor count
  const getGridColumns = (): number => {
    const count = floors.length;
    if (count === 1) return 1;
    if (count === 2) return 2;
    if (count <= 4) return 2;
    return 3;
  };

  const gridColumns = getGridColumns();

  // Handle empty state
  if (floors.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="9" y1="21" x2="9" y2="9" />
          </svg>
        </div>
        <h3 className={styles.emptyTitle}>층이 선택되지 않았습니다</h3>
        <p className={styles.emptyMessage}>
          왼쪽 패널에서 표시할 층을 선택하세요
        </p>
      </div>
    );
  }

  return (
    <div
      className={`${styles.container} ${styles[layout]} ${compact ? styles.compact : ''}`}
      style={
        layout === 'grid'
          ? { gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }
          : undefined
      }
    >
      {floors.map((floor) => (
        <FloorCanvas
          key={floor.id}
          floor={floor}
          layout={layout}
          synchronizedZoom={synchronizedZoom}
          externalZoom={synchronizedZoom ? masterZoomRef.current : undefined}
          onZoomChange={(zoom) => handleFloorZoomChange(floor.id, zoom)}
          bellClick={bellClick}
          compact={compact}
        />
      ))}
    </div>
  );
}
